
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./SuperadminLogin.css";

const Icon = ({ name, size = 19 }) => {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const icons = {
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2.5" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2.5" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    eye: (
      <>
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    eyeOff: (
      <>
        <path d="m3 3 18 18" />
        <path d="M10.6 5.1A11 11 0 0 1 12 5c6.4 0 10 7 10 7a16 16 0 0 1-4 4.7" />
        <path d="M6.2 6.2C3.5 8 2 12 2 12s3.6 7 10 7a10 10 0 0 0 4-.8" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      </>
    ),
    shield: (
      <>
        <path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    key: (
      <>
        <circle cx="8" cy="15" r="5" />
        <path d="m11.5 11.5 8-8L22 6l-3 3 2 2-3 3-2-2-2.5 2.5" />
      </>
    ),
  };

  return <svg {...props}>{icons[name]}</svg>;
};

const SuperadminLogin = () => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [token2FA, setToken2FA] = useState("");
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const [is2FARequired, setIs2FARequired] = useState(false);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro("");

    if (!email.trim() || !senha) {
      setErro("Preencha o e-mail e a senha.");
      return;
    }

    if (is2FARequired && !token2FA.trim()) {
      setErro("Informe o código de autenticação.");
      return;
    }

    setCarregando(true);

    try {
      sessionStorage.clear();

      const response = await api.post(
        "/superadmin/login",
        {
          email: email.trim(),
          senha,
          token_2fa: token2FA.trim(),
        },
        { withCredentials: true }
      );

      const {
        role,
        token,
        barbearia_id,
        email: responseEmail,
      } = response.data;

      if (role !== "superadmin") {
        setErro("Acesso não autorizado.");
        return;
      }

      sessionStorage.setItem("role", role);
      sessionStorage.setItem("barbearia_id", barbearia_id);
      sessionStorage.setItem("superadmin_token", token);
      sessionStorage.setItem("email", responseEmail);

      navigate("/sistema/gerenciamento");
    } catch (err) {
      const mensagem = (
        err.response?.data?.error || ""
      ).toLowerCase();

      if (
        mensagem.includes("2fa") ||
        mensagem.includes("token 2")
      ) {
        setIs2FARequired(true);
        setToken2FA("");
        setErro(
          mensagem.includes("obrigatório") ||
          mensagem.includes("obrigatorio")
            ? "Informe seu código 2FA."
            : "Código de autenticação inválido."
        );
      } else {
        setErro("Credenciais inválidas.");
      }
    } finally {
      setCarregando(false);
    }
  };

  return (
    <main className="cronos-admin-login">
      <div className="cal-grid" aria-hidden="true" />
      <div className="cal-glow cal-glow-top" aria-hidden="true" />
      <div className="cal-glow cal-glow-bottom" aria-hidden="true" />

      <div className="cal-version">
        <span className="cal-status-dot" />
        SISTEMA ADMINISTRATIVO
      </div>

      <section className="cal-card">
        <div className="cal-top-line" />

        <header className="cal-header">
          <div className="cal-logo">
            <img src="/logo_cronos.png" alt="Cronos Sistemas" />
          </div>

          <div className="cal-brand">
            <span className="cal-brand-name">CRONOS</span>
            <span className="cal-brand-subtitle">SISTEMAS</span>
          </div>

          <div className="cal-divider" />

          <span className="cal-access-label">SUPERADMIN</span>
        </header>

        <div className="cal-heading">
          <div className="cal-heading-icon">
            <Icon name="shield" size={21} />
          </div>

          <h1>Acesso restrito</h1>
          <p>Autentique-se para continuar.</p>
        </div>

        {erro && (
          <div className="cal-error" role="alert">
            <span className="cal-error-mark">!</span>
            {erro}
          </div>
        )}

        <form className="cal-form" onSubmit={handleLogin}>
          <label className="cal-field">
            <span>E-MAIL</span>

            <div className="cal-input">
              <Icon name="mail" />

              <input
                type="email"
                placeholder="Seu e-mail administrativo"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                disabled={carregando}
              />
            </div>
          </label>

          <label className="cal-field">
            <span>SENHA</span>

            <div className="cal-input">
              <Icon name="lock" />

              <input
                type={senhaVisivel ? "text" : "password"}
                placeholder="Sua senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                required
                disabled={carregando}
              />

              <button
                type="button"
                className="cal-eye"
                onClick={() => setSenhaVisivel(!senhaVisivel)}
                aria-label={senhaVisivel ? "Ocultar senha" : "Mostrar senha"}
                disabled={carregando}
              >
                <Icon name={senhaVisivel ? "eyeOff" : "eye"} size={18} />
              </button>
            </div>
          </label>

          {is2FARequired && (
            <label className="cal-field cal-2fa">
              <span>CÓDIGO DE AUTENTICAÇÃO</span>

              <div className="cal-input">
                <Icon name="key" />

                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Código 2FA"
                  value={token2FA}
                  onChange={(e) =>
                    setToken2FA(e.target.value.replace(/\s/g, ""))
                  }
                  maxLength={12}
                  required
                  autoFocus
                  disabled={carregando}
                />
              </div>
            </label>
          )}

          <button
            className="cal-submit"
            type="submit"
            disabled={carregando}
          >
            {carregando ? (
              <>
                <span className="cal-spinner" />
                AUTENTICANDO
              </>
            ) : (
              <>
                ENTRAR NO SISTEMA
                <span className="cal-submit-arrow">↗</span>
              </>
            )}
          </button>
        </form>

        <footer className="cal-card-footer">
          <Icon name="shield" size={14} />
          <span>CRONOS AGENDA</span>
          <span className="cal-footer-separator">/</span>
          <span>CRONOS SISTEMAS</span>
        </footer>
      </section>

      <div className="cal-bottom">
        <span>SR</span>
        <span className="cal-bottom-line" />
        <span>CRONOS SISTEMAS © {new Date().getFullYear()}</span>
      </div>
    </main>
  );
};

export default SuperadminLogin;
